import React from 'react';
import {Stack} from '@mui/material';
import {
  Body,
  designSystemToken,
  Headline,
  Icon,
  Tooltip,
} from '@lightricks/react-design-system';
import {
  getFeaturesTitle,
  getPlanFeatures,
} from '@contentlab/views/contentlab/pricing/utils';
import translate from '@/utils/translate';
import ContentLabPlanBundle from '@/types/models/pricing/contentLabPlanBundle';
import styles from './Features.module.scss';

export type FeaturesProps = {
  bundle: ContentLabPlanBundle;
  hasTitle?: boolean;
  seatsCount?: number;
  videosCount?: number;
  classname?: string;
};

type FeatureProps = {
  text: string | React.ReactNode;
  icon?: React.ReactNode;
};

const getFeatureColor = (feature: string) => {
  const featureColors: Record<string, string> = {
    'Custom number of credits': designSystemToken('semantic.fg.brand'),
  };

  return featureColors[feature] || designSystemToken('semantic.fg.primary');
};

function Feature({text, icon}: FeatureProps) {
  const color = getFeatureColor(text as string);
  return (
    <div className={styles.feature}>
      <Icon name="Actions-Accept" size="small" appearance="primary" />
      {typeof text === 'string' ? (
        <Body size="md" color={color}>
          {text}
        </Body>
      ) : (
        text
      )}
      {icon}
    </div>
  );
}

function VideosQuestionMark() {
  return (
    <Tooltip
      title={translate(
        '1 credit is used for every 30 seconds (or less) of generated video, across all features.'
      )}
      type="dark"
      placement="top"
      arrow
      PopperProps={{style: {width: '170px', whiteSpace: 'normal'}}}
    >
      <div>
        <Icon
          name="Navigation-Question-Line"
          size="small"
          appearance="primary"
          color={designSystemToken('semantic.bg.alpha-secondary')}
          className={styles.questionMarkIcon}
        />
      </div>
    </Tooltip>
  );
}

export function Features({
  bundle,
  hasTitle = true,
  seatsCount,
  videosCount,
  classname,
}: FeaturesProps) {
  const planFeatures = getPlanFeatures(bundle);

  return (
    <div className={`${styles.features} ${classname ?? ''}`}>
      {hasTitle && <Headline size="xxs">{getFeaturesTitle(bundle)}</Headline>}
      {videosCount && (
        <Feature
          text={
            <Stack direction="row" gap="3px">
              <Body size="md" color={designSystemToken('semantic.fg.brand')}>
                {translate('{count} credits', {count: videosCount})}
              </Body>
              <Body size="md">{translate('per month')}</Body>
            </Stack>
          }
          icon={<VideosQuestionMark />}
        />
      )}
      {seatsCount && (
        <Feature text={translate('{count} seats', {count: seatsCount})} />
      )}
      {planFeatures.map((feature: string) => (
        <Feature key={feature} text={feature} />
      ))}
    </div>
  );
}

export default Features;
